import React, {CSSProperties, Fragment, useEffect, useState} from "react";
import {
    ColumnSort,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    Row,
    SortingState,
    TableOptions,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import cn from "classnames";

import {ChevronDownIcon, ChevronUpIcon} from "@pg-design/icons-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {Text} from "@pg-design/text-module";
import {usePrevious} from "@pg-mono/hooks";

import {useRowSelection} from "../hooks/use_row_selection";
import {dataTableActions, DataTableDispatchType} from "../table_state_reducer";
import {IColumns} from "../types/IColumns";
import {ICustomRowRenderer} from "../types/ICustomRowRenderer";
import {TableGeneralSelectionType} from "../types/TableSelection";
import {getPageCount} from "../utils/get_page_count";
import {DataTableCellBooleanStatusRenderer} from "./cell_renderer/DataTableCellBooleanStatusRenderer";
import {DataTableDefaultCellRenderer} from "./cell_renderer/DataTableDefaultCellRenderer";
import {DataTableCellDateTimeRenderer} from "./renderers/DataTableCellDateTimeRenderer";
import {DataTableCellPictureWithName} from "./renderers/DataTableCellPictureWithName";
import {TableAllRowSelection} from "./TableAllRowSelection";
import {TableCell} from "./TableCell";
import {TableColumnVisibility} from "./TableColumnVisibility";

import * as styles from "./Table.module.css";

interface TablePropsBase<TDataSource extends object> {
    data: TableOptions<TDataSource>["data"];
    columns: IColumns<TDataSource, unknown>;
    filters?: Record<string, unknown>;
    showColumnVisibilityOptions?: boolean;
    onSortChange?: (sortingRule: ColumnSort | null) => void;
    onPageChange?: (page: number) => void;
    currentPage?: number;
    totalResults?: number;
    recordsPerPage?: number;
    dispatch?: DataTableDispatchType;
    defaultSortRules?: SortingState;
    noDataText?: string;
    columnVisibility: VisibilityState;
    setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
    onSelectedRecordChange?: (ids: string[]) => void;
    onExpandedStateChange?: (ids: string[]) => void;
    rowCustomStyle?: (row: Row<TDataSource>) => undefined | CSSProperties;
    rowCustomClassname?: (row: Row<TDataSource>) => undefined | string;
    rowDataTestId?: (row: Row<TDataSource>) => undefined | string;
    onRowClick?: (row: Row<TDataSource>) => void;
    expandedRowRenderer?: (row: Row<TDataSource>) => React.ReactNode;
    customExpandedRowWrapper?: boolean;
    className?: string;
    enableRowSelection?: (row: Row<TDataSource>) => boolean;
    manualSorting?: boolean;
    expandedExternalState?: {
        expanded: ExpandedState;
        setExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
    };
    getRowCanExpand?: (row: Row<TDataSource>) => boolean;
    headerVariant?: "light" | "dark";
    customRowRenderer?: ICustomRowRenderer<TDataSource>;
    sortDescFirst?: boolean;
}

export type TableProps<TDataSource extends object, UseInternalSelectionState extends boolean = true> = TablePropsBase<TDataSource> &
    TableGeneralSelectionType<TDataSource, UseInternalSelectionState>;

export const Table = <TDataSource extends object, UseInternalSelectionState extends boolean = true>(
    props: TableProps<TDataSource, UseInternalSelectionState>
) => {
    const headerVariant = props.headerVariant || "light";

    const [sorting, setSorting] = useState<SortingState>(props.defaultSortRules || []);
    const {rowSelection, onRowSelectionChange} = useRowSelection({
        data: props.data,
        filters: props.filters || {},
        initialSelectedRowIds: props.initialSelectedRowIds,
        areAllRecordsSelected: props.areAllRecordsSelected,
        getRowId: props.getRowId,
        rowSelection: props.rowSelection,
        onRowSelectionChange: props.onRowSelectionChange
    });
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const pageCount = getPageCount(props) ?? -1;
    const tableInstance = useReactTable<TDataSource>({
        columns: props.columns,
        data: props.data,
        state: {
            sorting,
            columnVisibility: props.columnVisibility,
            rowSelection,
            expanded: props.expandedExternalState?.expanded || expanded
        },
        manualPagination: true,
        manualSorting: props.manualSorting,
        pageCount,
        defaultColumn: {
            cell: DataTableDefaultCellRenderer,
            size: 0,
            minSize: 0
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: props.setColumnVisibility,
        onExpandedChange: props.expandedExternalState?.setExpanded || setExpanded,
        enableExpanding: true,
        enableRowSelection: props.enableRowSelection || true,
        onRowSelectionChange,
        getRowCanExpand: props.getRowCanExpand,
        getExpandedRowModel: getExpandedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: props.getRowId,
        debugTable: false,
        sortDescFirst: props.sortDescFirst || false
    });

    const prevSorting = usePrevious(sorting, sorting);

    useEffect(() => {
        if (prevSorting && prevSorting !== sorting) {
            const sortByRule = sorting[0] || null;

            if (props.onSortChange) {
                props.onSortChange(sortByRule);
            }

            if (props.dispatch) {
                props.dispatch(dataTableActions.changeSortBy(sortByRule));
            }
        }
    }, [sorting, prevSorting, props.onSortChange, props.dispatch]);

    const prevRowSelection = usePrevious(rowSelection, rowSelection);
    useEffect(() => {
        if (prevRowSelection !== rowSelection && (!props.areAllRecordsSelected || !tableInstance.getIsAllRowsSelected())) {
            props.onSelectedRecordChange?.(Object.keys(rowSelection));
        }
    }, [rowSelection, prevRowSelection]);

    useEffect(() => {
        props.onExpandedStateChange?.(Object.keys(props.expandedExternalState?.expanded || expanded));
    }, [props.expandedExternalState?.expanded, expanded]);

    const onChangePageClick = (pageNumber: number) => {
        tableInstance.setPageIndex(pageNumber - 1);

        if (props.onPageChange) {
            props.onPageChange(pageNumber);
        }

        if (props.dispatch) {
            props.dispatch(dataTableActions.changePage(pageNumber));
        }
    };

    const rows = tableInstance.getRowModel().rows;

    return (
        <>
            <TableColumnVisibility
                areAllColumnsVisible={tableInstance.getIsAllColumnsVisible()}
                areSomeColumnsVisible={tableInstance.getIsSomeColumnsVisible()}
                columns={tableInstance.getAllLeafColumns()}
                onToggleAll={tableInstance.getToggleAllColumnsVisibilityHandler()}
                style={{display: props.showColumnVisibilityOptions ? "block" : "none"}}
            />

            {props.onSelectAllRecords ? (
                <TableAllRowSelection
                    areAllRecordsSelected={!!props.areAllRecordsSelected}
                    areAllPageRecordsSelected={tableInstance.getIsAllRowsSelected()}
                    totalRecords={props.totalResults || 0}
                    perPageRecords={props.recordsPerPage || 0}
                    onSelectAllRecords={props.onSelectAllRecords}
                    onSelectOnlyPageRecords={() => {
                        if (tableInstance.getIsAllRowsSelected()) {
                            props.onSelectedRecordChange?.(Object.keys(rowSelection));
                        } else {
                            tableInstance.toggleAllRowsSelected(true);
                        }
                    }}
                />
            ) : null}

            <table className={cn(styles.table, props.className)}>
                <thead>
                    {tableInstance.getHeaderGroups().map((headerGroup) => {
                        return (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isSorted = header.column.getIsSorted();
                                    const isSortable = header.column.getCanSort();
                                    const size = header.column.getSize();

                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className={cn(
                                                styles.header,
                                                isSortable && styles.headerTextSortable,
                                                isSorted && styles.headerSorted,
                                                headerVariant === "dark" && styles.headerDark
                                            )}
                                            style={size ? {width: size} : {}}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <Text
                                                    variant="body_copy_2"
                                                    as="div"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className={styles.headerText}
                                                    strong={headerVariant === "dark"}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext()) as React.ReactNode}

                                                    {isSorted ? (
                                                        <span className={styles.sorted}>
                                                            {isSorted ? (
                                                                isSorted === "desc" ? (
                                                                    <ChevronDownIcon size="1.5" />
                                                                ) : (
                                                                    <ChevronUpIcon size="1.5" />
                                                                )
                                                            ) : (
                                                                ""
                                                            )}
                                                        </span>
                                                    ) : null}
                                                </Text>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={tableInstance.getAllColumns().length}>
                                <div className={styles.noData}>
                                    <Text variant="info_txt_1" align="center">
                                        {props.noDataText || "Brak dostępnych wyników do wyświetlenia"}
                                    </Text>
                                </div>
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => {
                        const rowCustomStyle = props.rowCustomStyle?.(row);
                        const rowCustomClassname = props.rowCustomClassname?.(row);
                        const rowRenderedByCustomRenderer = props.customRowRenderer
                            ? props.customRowRenderer(row, {
                                  className: cn(styles.bodyRow, Boolean(props.onRowClick) && styles.cursorPointer, rowCustomClassname),
                                  onClick: () => props.onRowClick?.(row),
                                  style: rowCustomStyle,
                                  cells: row.getVisibleCells().map((cell) => <TableCell key={cell.id} cell={cell} style={rowCustomStyle} />)
                              })
                            : null;

                        return (
                            <Fragment key={row.id}>
                                {rowRenderedByCustomRenderer ? (
                                    rowRenderedByCustomRenderer
                                ) : (
                                    <tr
                                        className={cn(styles.bodyRow, Boolean(props.onRowClick) && styles.cursorPointer, rowCustomClassname)}
                                        onClick={() => props.onRowClick?.(row)}
                                        data-testid={props.rowDataTestId?.(row)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return <TableCell key={cell.id} cell={cell} style={rowCustomStyle} />;
                                        })}
                                    </tr>
                                )}
                                {row.getIsExpanded() &&
                                    (props.customExpandedRowWrapper ? (
                                        props.expandedRowRenderer?.(row)
                                    ) : (
                                        <tr>
                                            <td colSpan={tableInstance.getAllColumns().length}>{props.expandedRowRenderer?.(row)}</td>
                                        </tr>
                                    ))}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>

            {pageCount !== -1 && (
                <div className={cn(styles.pagination, pageCount === 1 && styles.paginationEmpty)}>
                    <Pagination
                        pageCount={pageCount}
                        currentPage={props.currentPage || 1}
                        size={PaginationSize.SM}
                        onPageChange={onChangePageClick}
                        siblingsCount={2}
                    />
                </div>
            )}
        </>
    );
};

Table.Cell = {
    BooleanStatus: DataTableCellBooleanStatusRenderer
};

Table.Renderers = {
    PictureWithName: DataTableCellPictureWithName,
    DateTime: DataTableCellDateTimeRenderer
};
import React, {CSSProperties, Fragment, useEffect, useState} from "react";
import {
    ColumnSort,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    Row,
    SortingState,
    TableOptions,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import cn from "classnames";

import {ChevronDownIcon, ChevronUpIcon} from "@pg-design/icons-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {Text} from "@pg-design/text-module";
import {usePrevious} from "@pg-mono/hooks";

import {useRowSelection} from "../hooks/use_row_selection";
import {dataTableActions, DataTableDispatchType} from "../table_state_reducer";
import {IColumns} from "../types/IColumns";
import {ICustomRowRenderer} from "../types/ICustomRowRenderer";
import {TableGeneralSelectionType} from "../types/TableSelection";
import {getPageCount} from "../utils/get_page_count";
import {DataTableCellBooleanStatusRenderer} from "./cell_renderer/DataTableCellBooleanStatusRenderer";
import {DataTableDefaultCellRenderer} from "./cell_renderer/DataTableDefaultCellRenderer";
import {DataTableCellDateTimeRenderer} from "./renderers/DataTableCellDateTimeRenderer";
import {DataTableCellPictureWithName} from "./renderers/DataTableCellPictureWithName";
import {TableAllRowSelection} from "./TableAllRowSelection";
import {TableCell} from "./TableCell";
import {TableColumnVisibility} from "./TableColumnVisibility";

import * as styles from "./Table.module.css";

interface TablePropsBase<TDataSource extends object> {
    data: TableOptions<TDataSource>["data"];
    columns: IColumns<TDataSource, unknown>;
    filters?: Record<string, unknown>;
    showColumnVisibilityOptions?: boolean;
    onSortChange?: (sortingRule: ColumnSort | null) => void;
    onPageChange?: (page: number) => void;
    currentPage?: number;
    totalResults?: number;
    recordsPerPage?: number;
    dispatch?: DataTableDispatchType;
    defaultSortRules?: SortingState;
    noDataText?: string;
    columnVisibility: VisibilityState;
    setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
    onSelectedRecordChange?: (ids: string[]) => void;
    onExpandedStateChange?: (ids: string[]) => void;
    rowCustomStyle?: (row: Row<TDataSource>) => undefined | CSSProperties;
    rowCustomClassname?: (row: Row<TDataSource>) => undefined | string;
    rowDataTestId?: (row: Row<TDataSource>) => undefined | string;
    onRowClick?: (row: Row<TDataSource>) => void;
    expandedRowRenderer?: (row: Row<TDataSource>) => React.ReactNode;
    customExpandedRowWrapper?: boolean;
    className?: string;
    enableRowSelection?: (row: Row<TDataSource>) => boolean;
    manualSorting?: boolean;
    expandedExternalState?: {
        expanded: ExpandedState;
        setExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
    };
    getRowCanExpand?: (row: Row<TDataSource>) => boolean;
    headerVariant?: "light" | "dark";
    customRowRenderer?: ICustomRowRenderer<TDataSource>;
    sortDescFirst?: boolean;
}

export type TableProps<TDataSource extends object, UseInternalSelectionState extends boolean = true> = TablePropsBase<TDataSource> &
    TableGeneralSelectionType<TDataSource, UseInternalSelectionState>;

export const Table = <TDataSource extends object, UseInternalSelectionState extends boolean = true>(
    props: TableProps<TDataSource, UseInternalSelectionState>
) => {
    const headerVariant = props.headerVariant || "light";

    const [sorting, setSorting] = useState<SortingState>(props.defaultSortRules || []);
    const {rowSelection, onRowSelectionChange} = useRowSelection({
        data: props.data,
        filters: props.filters || {},
        initialSelectedRowIds: props.initialSelectedRowIds,
        areAllRecordsSelected: props.areAllRecordsSelected,
        getRowId: props.getRowId,
        rowSelection: props.rowSelection,
        onRowSelectionChange: props.onRowSelectionChange
    });
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const pageCount = getPageCount(props) ?? -1;
    const tableInstance = useReactTable<TDataSource>({
        columns: props.columns,
        data: props.data,
        state: {
            sorting,
            columnVisibility: props.columnVisibility,
            rowSelection,
            expanded: props.expandedExternalState?.expanded || expanded
        },
        manualPagination: true,
        manualSorting: props.manualSorting,
        pageCount,
        defaultColumn: {
            cell: DataTableDefaultCellRenderer,
            size: 0,
            minSize: 0
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: props.setColumnVisibility,
        onExpandedChange: props.expandedExternalState?.setExpanded || setExpanded,
        enableExpanding: true,
        enableRowSelection: props.enableRowSelection || true,
        onRowSelectionChange,
        getRowCanExpand: props.getRowCanExpand,
        getExpandedRowModel: getExpandedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: props.getRowId,
        debugTable: false,
        sortDescFirst: props.sortDescFirst || false
    });

    const prevSorting = usePrevious(sorting, sorting);

    useEffect(() => {
        if (prevSorting && prevSorting !== sorting) {
            const sortByRule = sorting[0] || null;

            if (props.onSortChange) {
                props.onSortChange(sortByRule);
            }

            if (props.dispatch) {
                props.dispatch(dataTableActions.changeSortBy(sortByRule));
            }
        }
    }, [sorting, prevSorting, props.onSortChange, props.dispatch]);

    const prevRowSelection = usePrevious(rowSelection, rowSelection);
    useEffect(() => {
        if (prevRowSelection !== rowSelection && (!props.areAllRecordsSelected || !tableInstance.getIsAllRowsSelected())) {
            props.onSelectedRecordChange?.(Object.keys(rowSelection));
        }
    }, [rowSelection, prevRowSelection]);

    useEffect(() => {
        props.onExpandedStateChange?.(Object.keys(props.expandedExternalState?.expanded || expanded));
    }, [props.expandedExternalState?.expanded, expanded]);

    const onChangePageClick = (pageNumber: number) => {
        tableInstance.setPageIndex(pageNumber - 1);

        if (props.onPageChange) {
            props.onPageChange(pageNumber);
        }

        if (props.dispatch) {
            props.dispatch(dataTableActions.changePage(pageNumber));
        }
    };

    const rows = tableInstance.getRowModel().rows;

    return (
        <>
            <TableColumnVisibility
                areAllColumnsVisible={tableInstance.getIsAllColumnsVisible()}
                areSomeColumnsVisible={tableInstance.getIsSomeColumnsVisible()}
                columns={tableInstance.getAllLeafColumns()}
                onToggleAll={tableInstance.getToggleAllColumnsVisibilityHandler()}
                style={{display: props.showColumnVisibilityOptions ? "block" : "none"}}
            />

            {props.onSelectAllRecords ? (
                <TableAllRowSelection
                    areAllRecordsSelected={!!props.areAllRecordsSelected}
                    areAllPageRecordsSelected={tableInstance.getIsAllRowsSelected()}
                    totalRecords={props.totalResults || 0}
                    perPageRecords={props.recordsPerPage || 0}
                    onSelectAllRecords={props.onSelectAllRecords}
                    onSelectOnlyPageRecords={() => {
                        if (tableInstance.getIsAllRowsSelected()) {
                            props.onSelectedRecordChange?.(Object.keys(rowSelection));
                        } else {
                            tableInstance.toggleAllRowsSelected(true);
                        }
                    }}
                />
            ) : null}

            <table className={cn(styles.table, props.className)}>
                <thead>
                    {tableInstance.getHeaderGroups().map((headerGroup) => {
                        return (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isSorted = header.column.getIsSorted();
                                    const isSortable = header.column.getCanSort();
                                    const size = header.column.getSize();

                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className={cn(
                                                styles.header,
                                                isSortable && styles.headerTextSortable,
                                                isSorted && styles.headerSorted,
                                                headerVariant === "dark" && styles.headerDark
                                            )}
                                            style={size ? {width: size} : {}}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <Text
                                                    variant="body_copy_2"
                                                    as="div"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className={styles.headerText}
                                                    strong={headerVariant === "dark"}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext()) as React.ReactNode}

                                                    {isSorted ? (
                                                        <span className={styles.sorted}>
                                                            {isSorted ? (
                                                                isSorted === "desc" ? (
                                                                    <ChevronDownIcon size="1.5" />
                                                                ) : (
                                                                    <ChevronUpIcon size="1.5" />
                                                                )
                                                            ) : (
                                                                ""
                                                            )}
                                                        </span>
                                                    ) : null}
                                                </Text>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={tableInstance.getAllColumns().length}>
                                <div className={styles.noData}>
                                    <Text variant="info_txt_1" align="center">
                                        {props.noDataText || "Brak dostępnych wyników do wyświetlenia"}
                                    </Text>
                                </div>
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => {
                        const rowCustomStyle = props.rowCustomStyle?.(row);
                        const rowCustomClassname = props.rowCustomClassname?.(row);
                        const rowRenderedByCustomRenderer = props.customRowRenderer
                            ? props.customRowRenderer(row, {
                                  className: cn(styles.bodyRow, Boolean(props.onRowClick) && styles.cursorPointer, rowCustomClassname),
                                  onClick: () => props.onRowClick?.(row),
                                  style: rowCustomStyle,
                                  cells: row.getVisibleCells().map((cell) => <TableCell key={cell.id} cell={cell} style={rowCustomStyle} />)
                              })
                            : null;

                        return (
                            <Fragment key={row.id}>
                                {rowRenderedByCustomRenderer ? (
                                    rowRenderedByCustomRenderer
                                ) : (
                                    <tr
                                        className={cn(styles.bodyRow, Boolean(props.onRowClick) && styles.cursorPointer, rowCustomClassname)}
                                        onClick={() => props.onRowClick?.(row)}
                                        data-testid={props.rowDataTestId?.(row)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return <TableCell key={cell.id} cell={cell} style={rowCustomStyle} />;
                                        })}
                                    </tr>
                                )}
                                {row.getIsExpanded() &&
                                    (props.customExpandedRowWrapper ? (
                                        props.expandedRowRenderer?.(row)
                                    ) : (
                                        <tr>
                                            <td colSpan={tableInstance.getAllColumns().length}>{props.expandedRowRenderer?.(row)}</td>
                                        </tr>
                                    ))}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>

            {pageCount !== -1 && (
                <div className={cn(styles.pagination, pageCount === 1 && styles.paginationEmpty)}>
                    <Pagination
                        pageCount={pageCount}
                        currentPage={props.currentPage || 1}
                        size={PaginationSize.SM}
                        onPageChange={onChangePageClick}
                        siblingsCount={2}
                    />
                </div>
            )}
        </>
    );
};

Table.Cell = {
    BooleanStatus: DataTableCellBooleanStatusRenderer
};

Table.Renderers = {
    PictureWithName: DataTableCellPictureWithName,
    DateTime: DataTableCellDateTimeRenderer
};
